import React, { useState, useEffect } from 'react';
import { Calendar, X, Check, Clock, Trash2, CalendarDays } from 'lucide-react';
import { Note } from '../../types';
import { getLocalDateString, shiftDays } from '../../lib/dateUtils';

interface NoteReminderModalProps {
  isOpen: boolean;
  note: Note | null;
  onClose: () => void;
  onSaveReminder: (noteId: string, reminderDate?: string) => void;
}

export const NoteReminderModal: React.FC<NoteReminderModalProps> = ({
  isOpen,
  note,
  onClose,
  onSaveReminder,
}) => {
  const [date, setDate] = useState('');

  useEffect(() => {
    if (note && note.reminder) {
      setDate(note.reminder);
    } else {
      setDate(shiftDays(getLocalDateString(), 1));
    }
  }, [note, isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !note) return null;

  const setQuickDate = (daysFromToday: number) => {
    setDate(shiftDays(getLocalDateString(), daysFromToday));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) return;
    onSaveReminder(note.id, date);
    onClose();
  };

  const handleRemoveReminder = () => {
    onSaveReminder(note.id, undefined);
    onClose();
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm animate-fade-in"
    >
      <div className="w-full max-w-sm bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-6 relative animate-scale-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="text-center space-y-2 mb-5">
          <div className="w-14 h-14 rounded-2xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center mx-auto shadow-lg shadow-purple-500/10">
            <CalendarDays className="w-7 h-7" />
          </div>

          <h3 className="text-lg font-black text-slate-900 dark:text-white">
            {note.reminder ? 'Edit Calendar Reminder' : 'Add to Calendar Reminder'}
          </h3>

          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 px-2 font-medium">
            {note.title || 'Untitled Note'}
          </p>

          <p className="text-[11px] text-slate-400">
            Set a reminder date to track this note on your Calendar schedule.
          </p>
        </div>

        {/* Quick Date Presets */}
        <div className="flex items-center gap-1.5 mb-4">
          <button
            type="button"
            onClick={() => setQuickDate(0)}
            className="flex-1 py-1.5 px-2 bg-slate-100 dark:bg-slate-800 hover:bg-purple-50 hover:text-purple-600 dark:hover:bg-purple-950/40 rounded-xl text-[11px] font-bold text-slate-600 dark:text-slate-300 transition-colors"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setQuickDate(1)}
            className="flex-1 py-1.5 px-2 bg-slate-100 dark:bg-slate-800 hover:bg-purple-50 hover:text-purple-600 dark:hover:bg-purple-950/40 rounded-xl text-[11px] font-bold text-slate-600 dark:text-slate-300 transition-colors"
          >
            Tomorrow
          </button>
          <button
            type="button"
            onClick={() => setQuickDate(7)}
            className="flex-1 py-1.5 px-2 bg-slate-100 dark:bg-slate-800 hover:bg-purple-50 hover:text-purple-600 dark:hover:bg-purple-950/40 rounded-xl text-[11px] font-bold text-slate-600 dark:text-slate-300 transition-colors"
          >
            In 1 Week
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-purple-500" />
              Reminder Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between gap-2 pt-2">
            {note.reminder ? (
              <button
                type="button"
                onClick={handleRemoveReminder}
                className="px-3 py-2.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl flex items-center gap-1 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Remove</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-purple-500/25 flex items-center justify-center gap-1.5 transition-all active:scale-95"
            >
              <Check className="w-4 h-4" />
              <span>Save Reminder</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
